import { useMemo } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { Eye } from 'lucide-react';
import { DataTable } from '@/components/molecules';
import { Button, State } from '@/components/atoms';
import { TemplateStatusBadge } from './template-status-badge';
import type { WhatsAppTemplate } from '@/types/whatsapp-template';

interface TemplatesTableProps {
  templates: WhatsAppTemplate[];
  onCreate: () => void;
  onView: (template: WhatsAppTemplate) => void;
}

export const TemplatesTable = ({
  templates,
  onCreate,
  onView,
}: TemplatesTableProps) => {
  const columns = useMemo<ColumnDef<WhatsAppTemplate>[]>(
    () => [
      {
        accessorKey: 'title',
        header: () => <div className="text-white font-semibold">Template</div>,
        cell: ({ row }) => (
          <div className="flex flex-col gap-0.5">
            <span className="font-medium">{row.original.title}</span>
            <span className="text-xs text-muted-foreground font-mono">
              {row.original.name}
            </span>
          </div>
        ),
      },
      {
        accessorKey: 'category',
        header: () => <div className="text-white font-semibold">Category</div>,
      },
      {
        accessorKey: 'status',
        header: () => <div className="text-white font-semibold">Status</div>,
        cell: ({ row }) => <TemplateStatusBadge status={row.original.status} />,
      },
      {
        accessorKey: 'language',
        header: () => <div className="text-white font-semibold">Language</div>,
        cell: ({ row }) => (
          <span className="uppercase text-sm">{row.original.language}</span>
        ),
      },
      {
        accessorKey: 'updatedAt',
        header: () => (
          <div className="text-white font-semibold">Last updated</div>
        ),
        cell: ({ row }) => (
          <span className="text-sm text-muted-foreground">
            {row.original.updatedAt
              ? new Date(row.original.updatedAt).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })
              : '—'}
          </span>
        ),
      },
      {
        id: 'actions',
        header: () => (
          <div className="text-white font-semibold uppercase text-xs">
            Actions
          </div>
        ),
        cell: ({ row }) => (
          <Button
            variant="outline"
            size="sm"
            className="text-xs gap-1.5"
            onClick={(event) => {
              event.stopPropagation();
              onView(row.original);
            }}
          >
            <Eye className="size-3.5" />
            View
          </Button>
        ),
      },
    ],
    [onView]
  );

  if (templates.length === 0) {
    return (
      <State
        description="You don't have any WhatsApp templates yet"
        button={{
          icon: 'Plus',
          text: 'Create template',
          onClick: onCreate,
        }}
      />
    );
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-accent">
      <DataTable columns={columns} data={templates} />
    </div>
  );
};
